import { useEffect, useState } from "react";
import { Ban, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatDate, formatIsoDay, formatTRY } from "@/lib/format";
import { Field, PayStatus } from "@/lib/finance";
import { useAuth } from "@/context/AuthContext";

const fmtVal = (v) => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

const Audit = ({ rows }) => (
  <div className="border border-slate-200 rounded max-h-40 overflow-y-auto" data-testid="payment-audit">
    {rows.map((a) => (
      <div key={a.id} className="px-3 py-1.5 border-b border-slate-100 last:border-0 text-[12px]">
        <span className="font-mono text-slate-400">{formatDate(a.timestamp)}</span> · <b>{a.user_name}</b> · {a.action === "create" ? "oluşturdu" : `${a.field}: ${fmtVal(a.old_value)} → ${fmtVal(a.new_value)}`}
        {a.note && <span className="text-slate-500"> – {a.note}</span>}
      </div>
    ))}
  </div>
);

export default function PaymentDetailDialog({ id, onClose, onChanged }) {
  const { can } = useAuth();
  const [p, setP] = useState(null);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [cancelNote, setCancelNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/tahsilatlar/${id}`).then(({ data }) => { setP(data); setDate(data.date); setNote(data.note || ""); });
  useEffect(() => { load().catch((e) => { toast.error(apiError(e)); onClose(); }); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const editable = p?.status === "aktif" && can("tahsilatlar", "edit");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="payment-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2">{p?.payment_no || "Tahsilat"} {p && <PayStatus status={p.status} testid="payment-detail-status" />}</DialogTitle></DialogHeader>
        {!p ? <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> : (
          <div className="space-y-3 text-[13px]">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {[["Cari", `${p.customer_code} · ${p.customer_title}`], ["Tutar", formatTRY(p.amount)], ["Tür", p.method_label], ["Tarih", formatIsoDay(p.date)],
                ["Hesap / Evrak", p.account_name || p.instrument_no || "—"], ["Tahsil Eden", p.collected_by_name],
                ["Eşleştirme", p.match_fifo ? `FIFO · ${p.allocations.length} kalem` : "Cari hesaba mahsup"], ["Not", p.note || "—"]].map(([k, v]) => (
                <div key={k}><div className="text-[11px] text-slate-400">{k}</div><div className="text-slate-800 truncate" title={v}>{v}</div></div>
              ))}
            </div>
            {p.status === "iptal" && <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800 text-[12px]" data-testid="payment-cancel-info">İptal: {p.cancel_note} · {p.cancelled_by_name} · {formatDate(p.cancelled_at)}</div>}
            {p.allocations.length > 0 && (
              <div className="border border-slate-200 rounded" data-testid="payment-allocations">
                {p.allocations.map((a) => <div key={a.entry_id} className="flex justify-between px-3 py-1 border-b border-slate-100 last:border-0 font-mono text-[12px]"><span>{a.doc_no} · vade {formatIsoDay(a.due_date)}</span><span>{formatTRY(a.amount)}</span></div>)}
              </div>
            )}
            {editable && (
              <div className="grid grid-cols-[150px_1fr_auto] gap-2 items-end">
                <Field label="Tarih"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-edit-date" /></Field>
                <Field label="Not"><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-edit-note" /></Field>
                <Button size="sm" variant="outline" className="h-9" disabled={busy} onClick={() => act(() => api.patch(`/tahsilatlar/${id}`, { date, note }), "Tahsilat güncellendi")} data-testid="payment-edit-save"><Save className="h-4 w-4 mr-1" />Kaydet</Button>
              </div>
            )}
            {editable && (
              <div className="grid grid-cols-[1fr_auto] gap-2 items-end p-3 rounded border border-rose-200 bg-rose-50/40">
                <Field label="İptal gerekçesi (zorunlu)"><Input value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} data-testid="payment-cancel-note" /></Field>
                <Button size="sm" variant="destructive" className="h-9" disabled={busy || cancelNote.trim().length < 3}
                  onClick={() => act(() => api.post(`/tahsilatlar/${id}/cancel`, { note: cancelNote }), "Tahsilat iptal edildi, ters kayıt oluşturuldu")} data-testid="payment-cancel-submit"><Ban className="h-4 w-4 mr-1" />Tahsilatı İptal Et</Button>
              </div>
            )}
            <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Değişiklik Geçmişi</div><Audit rows={p.audit} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
